/**
 * Qubi Zoom App - Main Entry Point
 *
 * This module handles WebSocket connections and Skir event parsing
 * for the Zoom sidebar panel.
 */

import { io } from 'socket.io-client';
import { BackToFrontEvent } from '../skirout/websocket_event.js';

// Configuration based on environment
const config = {
  development: {
    apiUrl: 'http://localhost:8009',
    webappUrl: 'http://localhost:8000'
  },
  staging: {
    apiUrl: 'https://api-staging.getqubi.com',
    webappUrl: 'https://app-staging.getqubi.com'
  },
  production: {
    apiUrl: 'https://api.getqubi.com',
    webappUrl: 'https://app.getqubi.com'
  }
};

// Detect environment from hostname
function getEnvironment() {
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'development';
  }
  if (hostname.includes('zoom-app-staging') || hostname.startsWith('zoom-staging.')) {
    return 'staging';
  }
  return 'production';
}

const env = getEnvironment();
const currentConfig = config[env];

// Socket.io instance
let socket = null;

// Callbacks for UI updates
let onMeetingStageChange = null;
let onConnected = null;
let onDisconnected = null;
let onError = null;

/**
 * Parse a SOIA BackToFrontEvent from raw data
 */
function parseBackToFrontEvent(data) {
  try {
    // fromJsonCode expects the raw JSON (string or already-parsed array)
    return BackToFrontEvent.serializer.fromJsonCode(data);
  } catch (e) {
    console.error('[QubiZoom] Failed to parse SOIA event:', e);
    return null;
  }
}

/**
 * Connect to the Qubi backend WebSocket
 */
export function connectToWebSocket(sessionToken) {
  if (!sessionToken) {
    console.error('[QubiZoom] No session token provided');
    if (onError) onError('No session token');
    return;
  }

  console.log('[QubiZoom] Connecting to WebSocket at', currentConfig.apiUrl);

  socket = io(currentConfig.apiUrl, {
    auth: {
      token: `Bearer ${sessionToken}`
    },
    transports: ['websocket'],
    reconnectionAttempts: 5,
    reconnectionDelay: 2000,
    timeout: 10000
  });

  socket.on('connect', () => {
    console.log(`[QubiZoom] WebSocket connected: ${socket.id}`);
    if (onConnected) onConnected(socket.id);

    // Send catch-up request to get any missed events
    socket.emit('catch_up', 0);
  });

  socket.on('disconnect', (reason) => {
    console.log(`[QubiZoom] WebSocket disconnected: ${reason}`);
    if (onDisconnected) onDisconnected(reason);
  });

  socket.on('connect_error', (error) => {
    console.error(`[QubiZoom] WebSocket connection error: ${error.message}`);
    if (onError) onError(error.message);
  });

  // Listen for SOIA events
  let callLiveNotified = false; // Track if we've already notified about live call
  socket.on('message', (data) => {
    const event = parseBackToFrontEvent(data);
    if (!event || event.union.kind === '?') {
      return; // Ignore unknown events
    }

    const eventKind = event.union.kind;

    if (eventKind === 'meeting_stage') {
      const meetingStageEvent = event.union.value;
      const stage = meetingStageEvent.newStage?.stage;
      console.log(`[QubiZoom] Meeting stage: ${stage?.kind}`);
      if (onMeetingStageChange) {
        onMeetingStageChange(stage?.kind, meetingStageEvent.meetingId);
      }
      // Reset callLiveNotified when we get an actual stage change
      if (stage?.kind === 'FINISHED' || stage?.kind === 'POST_PROCESSING_DONE') {
        callLiveNotified = false;
      }
    } else if (
      // These events indicate the call is live - but only notify once
      !callLiveNotified && (
        eventKind === 'transcript' ||
        eventKind === 'insight' ||
        eventKind === 'methodology_status' ||
        eventKind === 'calendar_extractions' ||
        eventKind === 'related_meetings' ||
        eventKind === 'realtime_insights' ||
        eventKind === 'coaching_cue'
      )
    ) {
      // First event that indicates call is live - notify once
      callLiveNotified = true;
      if (onMeetingStageChange) {
        const meetingId = event.union.value?.meetingId;
        onMeetingStageChange('BOT_IN_CALL_RECORDING', meetingId);
      }
    }
  });
}

/**
 * Disconnect from WebSocket
 */
export function disconnectWebSocket() {
  if (socket) {
    socket.disconnect();
    socket = null;
    console.log('[QubiZoom] WebSocket disconnected');
  }
}

/**
 * Set callback for meeting stage changes
 */
export function setOnMeetingStageChange(callback) {
  onMeetingStageChange = callback;
}

/**
 * Set callback for connection established
 */
export function setOnConnected(callback) {
  onConnected = callback;
}

/**
 * Set callback for disconnection
 */
export function setOnDisconnected(callback) {
  onDisconnected = callback;
}

/**
 * Set callback for errors
 */
export function setOnError(callback) {
  onError = callback;
}

/**
 * Get current configuration
 */
export function getConfig() {
  return { ...currentConfig, env };
}

/**
 * Check if currently connected
 */
export function isConnected() {
  return socket?.connected ?? false;
}
